import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import { lazy, Suspense, useEffect, useState } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import { SPLIT_ROUTES } from "./routes";
import Head from "./components/Head";

const ChatbotWidget = lazy(() => import("./components/ChatbotWidget"));
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then(m => ({ default: m.Toaster })),
);

/**
 * Returns true once the browser goes idle after hydration. Used to defer
 * mounting UI that isn't needed for first paint (chatbot, toast viewport),
 * keeping their JS out of the critical path entirely.
 */
function useIdleReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const start = () => setReady(true);
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(start, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(start, 2000);
    return () => window.clearTimeout(t);
  }, []);
  return ready;
}

function DeferredChatbot() {
  const ready = useIdleReady();
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ChatbotWidget />
    </Suspense>
  );
}

/**
 * Toasts fired before the Toaster mounts are held in sonner's internal
 * store and flush when the viewport mounts, so deferring is lossless.
 */
function DeferredToaster() {
  const ready = useIdleReady();
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
    </Suspense>
  );
}

function Router() {
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      {Object.entries(SPLIT_ROUTES).map(([path, { Component }]) => (
        <Route key={path} path={path} component={Component} />
      ))}
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <Head />
        <DeferredToaster />
        <Router />
        <DeferredChatbot />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
